import { HostListener, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  NgxPageVisibilityService,
  NgxPageVisibilityState,
} from '@ultimate/ngx-pagevisibility';
import { Subscription } from 'dexie';
import { filter, take, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';
import { StudyPreviewModalComponent } from '../clinic-studies/edit-clinical-study/components/study-preview-modal/study-preview-modal.component';
import { VideoModalComponent } from '../clinic-studies/edit-clinical-study/components/video-modal/video-modal.component';
import { StudyFile, StudyFileMediaInfoDto } from '../models/Study/study-file.model';
import { SaleHorseStudyBase, Study } from '../models/Study/study.model';
import {
  Activity,
  ActivityLogModel,
} from '../models/activity-log/activity-log.model';
import { BaseUnsubscribeComponent } from '../shared-new/components/base-unsubscribe/base-unsubscribe.component';
import { PdfModalComponent } from '../shared-new/components/pdf-modal/pdf-modal.component';
import { SalesFacade } from '../shared-new/store/sales/sales.facade';
import { StudiesFacade } from '../shared-new/store/studies/studies.facade';
import { ActivityLogService } from './activity-log.service';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class ViewerService extends BaseUnsubscribeComponent {
  isActivtyEnded: boolean = true;
  activityLogId: number = 0;
  public baseDicomUrl = environment.dicomViewer;
  private activityTimerRef: any = null;
  private isNewActivityStarted: boolean = false;
  activeSaleHorseStudy$ = this.studyFacade.activeSaleHorseStudy$;
  private idleTimeout: any;
  private idleTimeInSeconds = 110; // Set the idle time in seconds (2 minutes)
  private countdownSeconds = 10; // Countdown seconds for the swal alert
  private dicomStudyModalRef: NgbModalRef;

  private disabledLoggingPaths: string[] = [
    'clinic-studies/profile/',
  ];

  private _enableLogging: boolean = true;
  public set enableLogging(value: boolean) {
    // Disable logging on a few pages especially on clinical study page
    const isPathDisabled = this.disabledLoggingPaths.some(path => this.router.url.includes(path));
    if (isPathDisabled) {
      this._enableLogging = false;
    } else {
      this._enableLogging = value;
    }
  }
  public get enableLogging(): boolean {
    return this._enableLogging;
  }

  visibilityChangesSubscription: Subscription;
  constructor(
    private modalService: NgbModal,
    private studyFacade: StudiesFacade,
    private salesFacade: SalesFacade,
    private authService: AuthService,
    private activityLogService: ActivityLogService,
    private domSanitizer: DomSanitizer,
    private pageVisiblility: NgxPageVisibilityService,
    private router: Router
  ) {
    super();
  }

  private startActivity(
    activityId: number,
    studyFile: StudyFile,
    client: string,
    study: SaleHorseStudyBase,
    studyFileId?: number[],
    seriesInstanceUID?: string
  ): void {
    if (this.isActivtyEnded) {
      const activity: ActivityLogModel = {
        activityId: activityId,
        horseId: study.horseId,
        saleId: study.saleId,
        seriesInstanceUID:
          studyFile?.seriesInstanceUID ?? seriesInstanceUID ?? '',
        studyInstanceUID: studyFile?.studyInstanceUID,
        disclosedClient: client,
        jsonData: null,
        studyFileId: studyFileId,
      };
      if (this.enableLogging) {
        this.isActivtyEnded = false;
        this.studyFacade.startActivity(activity);
        this.studyFacade.startedActivityLogId$
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((activityLogId) => {
            if (activityLogId > 0) {
              this.activityLogId = activityLogId;
              //Task TW-5114: adding a timer to patch activity at an interval so that if user navigates away or closes window without closing the dialog, we have
              // an end time within an accuracy of a few seconds
              //first patch is 1 second
              this.clearTimer();
              this.activityTimerRef = setTimeout(this.patchActivity, 1000);
            }
          });
      }
    }
  }

  private clearTimer = () => {
    if (this.activityTimerRef !== null) {
      clearTimeout(this.activityTimerRef);
      this.activityTimerRef = null;
    }
  };

  private patchActivity = (cyclePatch: boolean = true) => {
    if (this.enableLogging) {
      this.clearTimer();
      this.activityLogService
        .updateActivityLog(this.activityLogId)
        .subscribe((response) => {
          if (response.isNewActivityCreated) {
            cyclePatch = false;
            this.isNewActivityStarted = true;
            this.clearTimer();
          }
          if (cyclePatch) {
            this.activityTimerRef = setTimeout(this.patchActivity, 5000);
          }
        });
    }
  };

  public endActivity(): void {
    this.isActivtyEnded = true;
    if (this.enableLogging) {
      if (!this.isNewActivityStarted) {
        this.patchActivity(false);
      }
      this.isNewActivityStarted = false;
    }
  }

  private _isVideoPlayerOpen: boolean = false;

  private cycleVideoConversionInfo(playerModalRef: any, studyFile: StudyFile, client: string, study: SaleHorseStudyBase, enableLogging: boolean) {
    setTimeout(() => this.checkVideoInfo(playerModalRef, studyFile, client, study, enableLogging), 3000);
  }

  checkVideoInfo = (playerModalRef: any, studyFile: StudyFile, client: string, study: SaleHorseStudyBase, enableLogging: boolean) => {
    if (!this._isVideoPlayerOpen) {
      return;
    }

    this.activityLogService
      .getStudyFileMediaConversionActivity(studyFile.studyFileId)
      .subscribe((response: StudyFileMediaInfoDto) => {
        if (!response.isConverted && !response.hasError) {
          this.cycleVideoConversionInfo(playerModalRef, studyFile, client, study, enableLogging);
        }
        if (typeof playerModalRef?.componentInstance !== 'undefined' || playerModalRef.componentInstance !== null) {
          playerModalRef.componentInstance.videoInfo = response;
          if (response.isConverted && !response.hasError) {
            this.enableLogging = enableLogging; //set to whatever we received as default
            this.startActivity(Activity.ScopeView, studyFile, client, study, [studyFile.studyFileId], null);
          }
        }
      });
  }

  playVideo(
    study: SaleHorseStudyBase,
    studyFile: StudyFile,
    disclosedClientName: string = null,
    needToLoadMemberAccess: boolean = true,
    callback: any = null,
    enableLogging: boolean = true
  ): void {

    let videoInfo: StudyFileMediaInfoDto;
    const modalRef = this.modalService.open(VideoModalComponent, {
      size: 'lg',
      centered: true,
      keyboard: false,
      backdrop: 'static',
    });

    this._isVideoPlayerOpen = true;
    if ((studyFile.conversionType ?? '') === '') {
      this.enableLogging = false; // don't log until file is converted and ready to be played.
      videoInfo = { conversionMessage: "Processing your video...", isConverted: false, hasError: false, studyFileId: studyFile.studyFileId };
      this.checkVideoInfo(modalRef, studyFile, disclosedClientName, study, enableLogging);

    }
    else {
      this.enableLogging = enableLogging;
      videoInfo = { conversionMessage: "", convertedUrl: studyFile.thumbURL, isConverted: true, hasError: false, studyFileId: studyFile.studyFileId };
    }

    if (study.saleId && study.horseId) {
      videoInfo.videoTitle = `Video - ${study.saleHorse.lot} - ${study.saleHorse.name}`;
    }
    else {
      videoInfo.videoTitle = `Video - ${study.patientName}`;
    }

    modalRef.componentInstance.videoInfo = videoInfo;

    if (study.saleId && study.horseId) {
      // start activity after model has opened.
      this.startActivity(
        Activity.ScopeView,
        studyFile,
        disclosedClientName,
        study,
        [studyFile.studyFileId],
        null
      );
    }

    modalRef.result.then(
      (result) => {
        this._isVideoPlayerOpen = false;
        if (study.saleId && study.horseId) {
          if (needToLoadMemberAccess) {
            this.salesFacade.loadSaleMemberAccess(study.saleId, study.horseId);
          }
          this.endActivity();
          if (callback && typeof callback == "function") {
            callback();
          }
        }
      },
      (reason) => {
        if (study.saleId && study.horseId) {
          if (needToLoadMemberAccess) {
            this.salesFacade.loadSaleMemberAccess(study.saleId, study.horseId);
          }
          this.endActivity();
          if (callback && typeof callback == "function") {
            callback();
          }
        }
      }
    );
  }

  openPdf(
    study: SaleHorseStudyBase,
    studyFile: StudyFile,
    disclosedClientName: string = null,
    needToLoadMemberAccess: boolean = true,
    callback: any = null,
    enableLogging: boolean = true
  ): void {
    this.enableLogging = enableLogging;
    const pdfURL = studyFile.thumbURL;
    const modalRef = this.modalService.open(PdfModalComponent, {
      size: 'lg',
      fullscreen: true,
      centered: true,
      keyboard: false,
      backdrop: 'static',
    });
    modalRef.componentInstance.url = pdfURL;

    if (study.saleHorse) {
      this.startActivity(
        Activity.ReportView,
        studyFile,
        disclosedClientName,
        study,
        [studyFile.studyFileId],
        null
      );
    }

    modalRef.result.then(
      (result) => {
        if (study.saleId && study.horseId) {
          if (needToLoadMemberAccess) {
            this.salesFacade.loadSaleMemberAccess(study.saleId, study.horseId);
          }
          this.endActivity();
          if (callback && typeof callback == "function") {
            callback();
          }

        }
      },
      (reason) => {
        if (study.saleId && study.horseId) {
          this.salesFacade.loadSaleMemberAccess(study.saleId, study.horseId);
          this.endActivity();
          if (callback && typeof callback == "function") {
            callback();
          }
        }
      }
    );
  }
  public startActivityDicomViewer(study: SaleHorseStudyBase, seriesInstanceUID?: string, disclosedClientName: string = null, studyFileIds: number[] = []) {
    this.visibilityChangesSubscription =
      this.pageVisiblility.changes$.subscribe(this.visibilityChangeCallback);
    this.startActivity(
      Activity.XRayView,
      null,
      disclosedClientName,
      study,
      studyFileIds,
      seriesInstanceUID
    );
  }
  public openDicomViewerForStudy(
    study: SaleHorseStudyBase,
    seriesInstanceUID?: string,
    disclosedClientName: string = null,
    studyFileIds: number[] = [],
    enableLogging: boolean = true
  ) {
    this.enableLogging = enableLogging;
    let viewerGuid = null;
    // when in clinical study profile page, only use viewerGuid of the study not for the sale/horse.
    if ((study as any).viewerGuid) {
      viewerGuid = (study as any).viewerGuid;
    } else if (
      study.saleHorse?.viewerGuid !== null &&
      typeof study.saleHorse?.viewerGuid !== 'undefined'
    ) {
      viewerGuid = study.saleHorse.viewerGuid;
    }

    this.dicomStudyModalRef = this.modalService.open(
      StudyPreviewModalComponent,
      {
        centered: true,
        keyboard: false,
        size: 'xl',
        backdrop: 'static',
        fullscreen: true,
        //windowClass: 'dicom-viewer-modal',
      }
    );

    this.dicomStudyModalRef.componentInstance.url = `${this.baseDicomUrl
      }/?share=${viewerGuid}&token=${this.authService.getToken()}`;
    this.dicomStudyModalRef.componentInstance.viewerGuid = viewerGuid;
    this.dicomStudyModalRef.componentInstance.study = study;
    this.dicomStudyModalRef.componentInstance.studyFileIds = studyFileIds;
    this.dicomStudyModalRef.componentInstance.disclosedClientName = disclosedClientName;
    if ((study.saleHorse?.saleName ?? '')) {
      this.dicomStudyModalRef.componentInstance.horseName = `${(study.saleHorse?.saleName ?? '')} - ${(study.sale?.lotOrHipText ?? '')} ${(study.saleHorse?.lotNumber ?? '')}`;
    } else {
      if ((study as Study).patientName) {
        this.dicomStudyModalRef.componentInstance.horseName = `Study - ${(study as Study).patientName}`;
      } else {
        this.dicomStudyModalRef.componentInstance.horseName = `Study`;
      }
    }

    if (study.saleId && study.horseId) {
      // start activity after model has opened.
      this.startActivityDicomViewer(study, seriesInstanceUID, disclosedClientName, studyFileIds);
    }

    this.dicomStudyModalRef.result.then(
      (result) => {
        if (study.saleId && study.horseId) {
          this.resetIdleTimer();
          if (this.visibilityChangesSubscription) {
            this.visibilityChangesSubscription.unsubscribe();
          }
          this.endActivity();
        }
      },
      (reason) => {
        if (study.saleId && study.horseId) {
          this.resetIdleTimer();
          if (this.visibilityChangesSubscription) {
            this.visibilityChangesSubscription.unsubscribe();
          }
          this.endActivity();
        }
      }
    );
  }
  private startIdleTimer() {
    // Set a timeout to show swal alert after the specified idle time
    if (!this.authService.IsSalesCompany()) {
      if (this.idleTimeout) {
        this.resetIdleTimer();
      }
      this.idleTimeout = setTimeout(() => {
        this.showIdleAlert();
      }, this.idleTimeInSeconds * 1000);
    }
  }

  private resetIdleTimer() {
    if (!this.authService.IsSalesCompany()) {
      // Reset the idle timer on mouse move or key press
      clearTimeout(this.idleTimeout);
      this.idleTimeout = null;
    }
  }

  private showIdleAlert() {
    if (!this.authService.IsSalesCompany()) {
      let countdown = this.countdownSeconds;

      const updateTimer = () => {
        if (Swal.isVisible()) {
          Swal.update({
            title: 'Idle Alert',
            text: `Your Viewer session has been idle for 2 minutes. Do you want to continue? We'll close the viewer in ${countdown} seconds.`,
          });
        }

        if (countdown > 0) {
          countdown--;
          setTimeout(updateTimer, 1000);
        }
      };

      Swal.fire({
        title: 'Idle Alert',
        text: `Your Viewer session has been idle for 2 minutes. Do you want to continue? We'll close the viewer in ${countdown} seconds.`,
        showCancelButton: true,
        confirmButtonText: 'Continue',
        cancelButtonText: 'Close',
        reverseButtons: true,
        timerProgressBar: true,
        timer: countdown * 1000,
        allowOutsideClick: false,
      }).then((result) => {
        if (result.isConfirmed) {
          this.resetIdleTimer();
          Swal.close();
        } else {
          this.endActivity();
          this.dicomStudyModalRef.close();
          Swal.fire(
            'Warning',
            'We have closed the study viewer because you have been idle for more than 2 minutes.',
            'warning'
          );
        }
      });

      updateTimer();
    }
  }
  private visibilityChangeCallback = (state: NgxPageVisibilityState) => {
    if (!state.visible) {
      // Inactive window
      this.startIdleTimer();
    } else {
      this.resetIdleTimer();
    }
  };

  public async openDicomViewrForHorseStudy(
    saleId: number,
    horseId: number,
    seriesInstanceUID: string,
    disclosedClientName: string = null
  ) {
    this.studyFacade.getSaleHorseStudies(saleId, horseId);
    let activeStudy = this.studyFacade.activeSaleHorseStudy$
      .pipe(
        filter((study) => !!study),
        take(1)
      )
      .subscribe((study) => {
        this.openDicomViewerForStudy(
          study,
          seriesInstanceUID,
          disclosedClientName
        );
      });
  }

  @HostListener('mousemove', ['$event'])
  @HostListener('keydown', ['$event'])
  onUserActivity() {
    // Reset the idle timer on mouse move or key press
    this.resetIdleTimer();
    this.startIdleTimer(); // start again timer to check user idle state
  }
}
